import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ModeBadge from "@/components/ModeBadge";
import {
  Search,
  Filter,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileAudio,
  RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAssessments, useCohorts, useRetryAssessment, type AssessmentListItem } from "@/lib/api";

type StatusFilter = 'all' | 'pending' | 'completed' | 'processing';

function getErrorCategory(statusMessage: string): string {
  const msg = statusMessage.toLowerCase();
  if (msg.includes('speech') || msg.includes('transcri') || msg.includes('assemblyai') || msg.includes('stt')) {
    return 'Speech recognition failed';
  }
  if (msg.includes('llm') || msg.includes('openai') || msg.includes('groq') || msg.includes('scoring') || msg.includes('report')) {
    return 'AI scoring failed';
  }
  if (msg.includes('upload') || msg.includes('recording') || msg.includes('s3') || msg.includes('file')) {
    return 'Recording missing or corrupt';
  }
  if (msg.includes('timeout') || msg.includes('timed out')) {
    return 'Processing timed out';
  }
  if (msg.includes('audio') || msg.includes('format') || msg.includes('codec')) {
    return 'Unsupported audio format';
  }
  return 'Processing error';
}

export default function TeacherReviewListPage() {
  const { data: assessments, isLoading: loadingAssessments } = useAssessments();
  const { data: cohorts, isLoading: loadingCohorts } = useCohorts();
  const retryAssessment = useRetryAssessment();

  const [searchQuery, setSearchQuery] = useState('');
  const [cohortFilter, setCohortFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Filter assessments based on search, cohort, and status
  const filteredAssessments = useMemo(() => {
    if (!assessments) return [];

    return assessments.filter((assessment) => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        assessment.student_name.toLowerCase().includes(searchQuery.toLowerCase());

      // Cohort filter
      const matchesCohort = cohortFilter === 'all' || assessment.cohort_name === cohortFilter;

      // Status filter
      let matchesStatus = true;
      if (statusFilter === 'pending') {
        matchesStatus = assessment.status === 'completed';
      } else if (statusFilter === 'completed') {
        matchesStatus = assessment.status === 'signed_off';
      } else if (statusFilter === 'processing') {
        matchesStatus = assessment.status === 'processing';
      }

      return matchesSearch && matchesCohort && matchesStatus;
    });
  }, [assessments, searchQuery, cohortFilter, statusFilter]);

  // Get unique cohort names for filter options
  const cohortOptions = useMemo(() => {
    if (!assessments) return [];
    const uniqueCohorts = new Set(assessments.map(a => a.cohort_name));
    return Array.from(uniqueCohorts).sort();
  }, [assessments]);

  // Count pending reviews
  const pendingCount = useMemo(() => {
    if (!assessments) return 0;
    return assessments.filter(a => a.status === 'completed').length;
  }, [assessments]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <AlertCircle className="w-4 h-4 text-warning" />;
      case 'signed_off':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-info animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Not Started';
      case 'signed_off':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case 'draft':
        return 'Draft';
      case 'recording':
        return 'Recording';
      case 'uploading':
        return 'Uploading';
      case 'under_review':
        return 'In Review';
      case 'draft_ready':
        return 'Ready for Review';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'completed':
        return 'outline';
      case 'signed_off':
        return 'default';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getModeBadgeMode = (mode: string): "presentational" | "exploratory" | "collaborative" | "dialogic" | "persuasive" | "reflective" => {
    switch (mode) {
      case 'presenting':
        return 'presentational';
      case 'explaining':
        return 'exploratory';
      case 'persuading':
        return 'persuasive';
      default:
        return 'presentational';
    }
  };

  const isLoading = loadingAssessments || loadingCohorts;

  return (
    <AppLayout role="teacher">
      <div className="space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">
                Review Assessments
              </h1>
              <p className="text-muted-foreground">
                Review and sign off on student assessments
              </p>
            </div>
            {pendingCount > 0 && (
              <Badge variant="outline" className="gap-2 text-warning border-warning">
                <AlertCircle className="w-3.5 h-3.5" />
                {pendingCount} pending
              </Badge>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by student name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Cohort Filter */}
                <Select value={cohortFilter} onValueChange={setCohortFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="All Cohorts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cohorts</SelectItem>
                    {cohortOptions.map((cohort) => (
                      <SelectItem key={cohort} value={cohort}>
                        {cohort}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                  <SelectTrigger className="w-full sm:w-[160px]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Results */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                {filteredAssessments.length} Assessment{filteredAssessments.length !== 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAssessments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileAudio className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No assessments found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {searchQuery || cohortFilter !== 'all' || statusFilter !== 'all'
                      ? 'Try adjusting your filters'
                       : 'Assessments will appear here when students complete tasks'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Cohort</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssessments.map((assessment) => (
                      <TableRow key={assessment.id} className="group">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                              <span className="text-xs font-medium text-primary-foreground">
                                {assessment.student_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <span className="font-medium">{assessment.student_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {assessment.cohort_name}
                        </TableCell>
                        <TableCell>
                          <ModeBadge mode={getModeBadgeMode(assessment.mode)} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(assessment.status)}
                            <Badge variant={getStatusVariant(assessment.status)}>
                              {getStatusLabel(assessment.status)}
                            </Badge>
                          </div>
                          {assessment.status === "error" && (
                            <div className="mt-1 flex items-center gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="text-xs text-destructive cursor-help underline decoration-dotted">
                                      {assessment.status_message
                                        ? getErrorCategory(assessment.status_message)
                                        : "Processing error"}
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="font-medium mb-1">
                                      {assessment.status_message || "No details available"}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Failed{" "}
                                      {new Date(assessment.updated_at).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {assessment.has_recording && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 px-1.5 text-xs gap-1 text-muted-foreground hover:text-foreground"
                                  disabled={retryAssessment.isPending}
                                  onClick={() => retryAssessment.mutate(assessment.id)}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Retry
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(assessment.uploaded_at || assessment.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/teacher/review/${assessment.id}`}>
                            <Button 
                              size="sm" 
                              variant={assessment.status === 'completed' ? 'default' : 'outline'}
                              className="gap-1"
                            >
                              {assessment.status === 'completed' ? 'Review' : 'View'}
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AppLayout>
  );
}
